import React, { ReactNode, useState } from "react";
import Snackbar from "@mui/material/Snackbar";
import { SnackBarContext } from "./FeedBackContext";
import Alert, { AlertProps } from "@mui/material/Alert";
import AlertDialog from "@/components/AlertDialog";
import { styled } from "@mui/material/styles";

const BUTTON_LINK_TAB_ID = "coyn-open-tab-button";

const CustomStyledSnackbar = styled(Snackbar)(({ theme }) => ({
  "& .MuiSnackbar-root": {
    backgroundColor: theme.palette.surface.dark,
    color: theme.palette.textMain.dark2,
    borderRadius: "8px",
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.12)",
    padding: "8px 12px",
  },
  "& .MuiAlert-message": {
    fontSize: "14px",
    fontWeight: "400",
    display: "flex",
    alignItems: "center",
  },
  "& .MuiAlert-action": {
    color: theme.palette.textMain.dark2,
    "& .MuiIconButton-root": {
      color: theme.palette.textMain.dark2,
    },
    fontSize: "10px",
  },
}));

const CustomStyledAlert = styled(Alert)(({ theme }) => ({
  backgroundColor: theme?.palette?.surface?.dark,
  color: theme.palette.textMain.dark2,
  borderRadius: "8px",
  boxShadow: "none",
}));

export const FeedBackProvider = ({ children }: { children: ReactNode }) => {
  const [isSnackBarOpen, setIsSnackBarOpen] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState("");
  const [snackBarSeverity, setSnackBarSeverity] =
    useState<AlertProps["severity"]>("error");
  const [isSimpleAlertOpen, setIsSimpleAlertOpen] = useState(false);
  const [simpleAlertMessage, setSimpleAlertMessage] = useState("");

  const showSnackBar = (
    message: string,
    severity: AlertProps["severity"] = "info",
  ) => {
    setSnackBarSeverity(severity);
    setSnackBarMessage(message);
    setIsSnackBarOpen(true);
  };

  const hideSnackBar = () => {
    setSnackBarMessage("");
    setIsSnackBarOpen(false);
  };

  const showSimpleAlert = (message: string) => {
    setSimpleAlertMessage(message);
    setIsSimpleAlertOpen(true);
  };

  const hideSimpleAlert = () => {
    setSimpleAlertMessage("");
    setIsSimpleAlertOpen(false);
  };

  const openLinkInTab = (link: string) => {
    if (!link) return;
    const aElement = document.getElementById(
      BUTTON_LINK_TAB_ID,
    ) as HTMLLinkElement;
    aElement.href = link;
    aElement.dispatchEvent(new MouseEvent("click", { bubbles: true }));
  };

  return (
    <SnackBarContext.Provider
      value={{
        showSnackBar,
        hideSnackBar,
        showSimpleAlert,
        hideSimpleAlert,
        openLinkInTab,
      }}
    >
      {children}
      <CustomStyledSnackbar
        open={isSnackBarOpen}
        autoHideDuration={6000}
        security={snackBarSeverity}
        onClose={hideSnackBar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <CustomStyledAlert
          icon={false}
          onClose={hideSnackBar}
          severity={snackBarSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackBarMessage}
        </CustomStyledAlert>
      </CustomStyledSnackbar>
      <AlertDialog
        open={isSimpleAlertOpen}
        title="Warning"
        description={simpleAlertMessage}
        confirmText="Accept"
        onConfirm={hideSimpleAlert}
        onCancel={hideSimpleAlert}
      />
      <a
        hidden
        id={BUTTON_LINK_TAB_ID}
        target="_blank"
        rel="noopener noreferrer"
      />
    </SnackBarContext.Provider>
  );
};
